import React, { useEffect, useState, useRef, useCallback } from "react";
import { Dropdown, Form, Spinner } from "react-bootstrap";
import { ChevronDown, Check, X } from "lucide-react";
import fill_chevron_down from "../../assets/png/fill_chevron_down.png";
import ReactDOM from "react-dom";
import { apiCallGeneric } from "../../services/api-service.js";
import "./common.css";

const CommonDropdown = ({
  apiUrl,
  placeholder = "Select",
  onSelect,
  value,
  searchView = false,
  disabled = false,
  emptyMessage = "No results found",
  width = "100%",
  borderRadius,
  isError = false,
}) => {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchDebounceTimer = useRef(null);

  // Fetch from API with debouncing
  const fetchData = useCallback(
    async (term = "") => {
      if (!apiUrl) {
        setItems([]);
        return;
      }

      setLoading(true);

      try {
        const response = await apiCallGeneric("get", apiUrl, null, {
          params: { term },
        });

        if (response.status === 200 && response.data.success) {
          setItems(response.data.suggestionlist || []);
        } else {
          setItems([]);
        }
      } catch (err) {
        console.error("Error fetching options:", err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl],
  );

  // Lazy-load options only when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchData(searchTerm);
    }
  }, [isOpen, fetchData, searchTerm]);

  // Sync selected value from parent
  useEffect(() => {
    setSelected(value);
  }, [value]);

  // Handle search with debouncing (300ms delay)
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    // Clear existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    // Set new timer for debounced API call
    searchDebounceTimer.current = setTimeout(() => {
      fetchData(term);
    }, 300);
  };

  // Handle selection
  const handleSelect = (item) => {
    setSelected(item);
    setSearchTerm("");
    if (onSelect) onSelect(item);
  };

  // Handle clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    setSearchTerm("");
    if (onSelect) onSelect(null);
  };

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    placement: "bottom",
  });
  const containerRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const menuMaxHeight = 350; // approx max height of the menu

      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < menuMaxHeight && spaceAbove > spaceBelow) {
        setMenuPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          placement: "top",
        });
      } else {
        setMenuPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
          placement: "bottom",
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [isOpen, updatePosition]);

  return (
    <div ref={containerRef} style={{ width: width }}>
      <Dropdown show={isOpen} onToggle={(nextShow) => setIsOpen(nextShow)}>
        <Dropdown.Toggle
          variant="success"
          id="dropdown-basic"
          disabled={disabled}
          className={`${
            searchView === false
              ? "bg-transparent text-black"
              : "bg-white text-purple assigned-dropdown rounded-4 px-4"
          } d-flex justify-content-between align-items-center dp-toggle transition-all`}
          style={{
            width: "100%",
            border: isError ? "1px solid #EF4444" : searchView ? "1px solid #e0e0e0" : "1px solid #D4DDE7",
            borderRadius: borderRadius || (searchView ? "1.5rem" : "10px"),
            transition: "all 0.2s ease-in-out",
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
            outline: "none",
            boxShadow: "none",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#6c5ce7";
            e.currentTarget.style.boxShadow =
              "0 0 0 0.2rem rgba(108, 92, 231, 0.25)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = searchView
              ? "#e0e0e0"
              : "#D4DDE7";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <span
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              color: selected ? "#212129" : "#888888",
              fontWeight: "400",
              fontSize: "13px",
            }}
          >
            {selected ? selected.label : placeholder}
          </span>
          <span className="d-flex align-items-center gap-2">
            {searchView === true && selected && (
              <X
                size={16}
                onClick={handleClear}
                style={{ cursor: "pointer", color: "#888888" }}
                className="hover-opacity"
              />
            )}
            {searchView === false ? (
              <ChevronDown
                size={18}
                style={{
                  transition: "transform 0.2s ease-in-out",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            ) : (
              <img
                src={fill_chevron_down}
                alt="dropdown"
                style={{
                  transition: "transform 0.2s ease-in-out",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            )}
          </span>
        </Dropdown.Toggle>

        {isOpen &&
          ReactDOM.createPortal(
            <Dropdown.Menu
              show={isOpen}
              className="p-2 shadow-lg border-0"
              style={{
                position: "fixed",
                top:
                  menuPosition.placement === "bottom"
                    ? `${menuPosition.top + 5}px`
                    : "auto",
                bottom:
                  menuPosition.placement === "top"
                    ? `${window.innerHeight - menuPosition.top + 5}px`
                    : "auto",
                left: `${menuPosition.left}px`,
                width: `${menuPosition.width}px`,
                maxHeight: "320px",
                overflowY: "auto",
                zIndex: 10001,
                borderRadius: "12px",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                backgroundColor: "white",
                margin: 0,
              }}
            >
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
                className="mb-2 fs-13"
                autoFocus
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "14px",
                  transition: "all 0.2s ease-in-out",
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6c5ce7";
                  e.target.style.boxShadow =
                    "0 0 0 0.2rem rgba(108, 92, 231, 0.15)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e0e0e0";
                  e.target.style.boxShadow = "none";
                }}
              />

              {loading && (
                <div
                  className="d-flex align-items-center justify-content-center py-4"
                  style={{ color: "#888888" }}
                >
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginRight: "8px" }}
                  />
                  <span className="fs-14">Loading...</span>
                </div>
              )}

              {!loading && items.length > 0 ? (
                <div style={{ maxHeight: "240px", overflowY: "auto" }}>
                  {items.map((item) => (
                    <Dropdown.Item
                      key={item.value}
                      onClick={() => {
                        handleSelect(item);
                        setIsOpen(false);
                      }}
                      className="d-flex justify-content-between align-items-center text-black"
                      style={{
                        padding: "10px 12px",
                        borderRadius: "6px",
                        margin: "2px 0",
                        fontSize: "14px",
                        fontWeight: "500",
                        transition: "all 0.15s ease-in-out",
                        backgroundColor:
                          selected && selected.value === item.value
                            ? "#f0ebff"
                            : "transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (!selected || selected.value !== item.value) {
                          e.currentTarget.style.backgroundColor = "#f8f9fa";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          selected && selected.value === item.value
                            ? "#f0ebff"
                            : "transparent";
                      }}
                    >
                      <span>{item.label}</span>
                      {selected && selected.value === item.value && (
                        <Check size={16} color="#6c5ce7" strokeWidth={2.5} />
                      )}
                    </Dropdown.Item>
                  ))}
                </div>
              ) : !loading ? (
                <div
                  className="text-center py-4"
                  style={{ color: "#888888", fontSize: "14px" }}
                >
                  {emptyMessage}
                </div>
              ) : null}
            </Dropdown.Menu>,
            document.body,
          )}
      </Dropdown>
    </div>
  );
};

export default CommonDropdown;
